import { Color, CompanySettings, DateUtil, Logger, LogManager, Model } from "@mcleod/core";
import {
    ChangeEvent, DataSourceAction, DataSourceExecutionEvent, DropdownItem, LookupModelSearchEvent, SeparatorPanel,
    Textbox
} from "@mcleod/components";
import { AutogenLayoutUserGeneral } from "./autogen/AutogenLayoutUserGeneral";
import { ColorSelectorUtil } from "../ColorSelectorUtil";
import { UserUtil } from "./UserUtil";

export class UserGeneral extends AutogenLayoutUserGeneral {
    private static logger: Logger;
    private _evenRowColor: Color;
    private _oddRowColor: Color;
    private separatorPanel: SeparatorPanel;

    override onLoad() {
        this.setupTimeFormats();
        this.setupEmailOverrideComponents();
    }

    private setupTimeFormats() {
        const civilianFormat = new DropdownItem("C", "Civilian").setAddlValue("format", "hh:mma");
        const militaryFormat = new DropdownItem("M", "Military").setAddlValue("format", "HHmm");
        this.textTimeFormat.items = [ civilianFormat, militaryFormat ];
    }

    private setupEmailOverrideComponents() {
        // Show/hide override switch based on admin access
        UserUtil.setAdminComponentVisibility(this.switchEmailOverrideCompany);

        // Bind components to main user settings dataSource
        this.layoutEmailSettings.bindToDataSource(this.sourceUserSettings);
        this.layoutEmailSettings.textboxEmail.enabled = false;

        // Align override's switch with neighboring textbox
        this.switchEmailOverrideCompany.captionLabelMarginTop = -4;
        this.switchEmailOverrideCompany.captionLabelMarginBottom = 6;

        // Create the separator panel and display it if necessary
        this.separatorPanel = new SeparatorPanel(this, true, 100);
        this.separatorPanel.style.alignSelf = "stretch";
        this.insertBefore(this.separatorPanel, this.panelUserGeneralRight);
        this.syncEmailOverrideFieldVisibility();
    }

    textDateFormatOnChange() {
        this.updateSampleDateTime();
    }

    textTimeFormatOnChange() {
        this.updateSampleDateTime();
    }

    private updateSampleDateTime() {
        const date = new Date();
        date.setFullYear(date.getFullYear(), 11, 31);
        date.setHours(16);
        date.setMinutes(35);
        let format = this.textDateFormat.selectedItem?.value ?? "MM/dd/yyyy";
        format += " ";
        format += this.textTimeFormat.selectedItem?.getAddlValue("format") ?? "hh:mma";
        this.labelSampleDateTime.text = DateUtil.formatDateTime(date, format);
    }

    async buttonEvenColorOnClick() {
        const selectedColor = await ColorSelectorUtil.promptForColor();
        UserGeneral.getLog().debug("Selected even row color: %o", selectedColor);
        this.evenRowColor = selectedColor;
    }

    async buttonOddColorOnClick() {
        const selectedColor = await ColorSelectorUtil.promptForColor();
        UserGeneral.getLog().debug("Selected odd row color: %o", selectedColor);
        this.oddRowColor = selectedColor;
    }

    buttonClearEvenColorOnClick() {
        this.evenRowColor = null;
    }

    buttonClearOddColorOnClick() {
        this.oddRowColor = null;
    }

    private get evenRowColor(): Color {
        return this._evenRowColor;
    }

    private set evenRowColor(value: Color) {
        if (value === this._evenRowColor) {
            return;
        }
        this._evenRowColor = value;
        this.sourceUserSettings.activeRow.set("table_even_row_color", this.evenRowColor);
        // Don't set color when in the designer...we don't want to serialize that color as part of the layout
        if (this._designer == null) {
            this.panelEvenColorDisplay.backgroundColor = this._evenRowColor ?? "";
        }
    }

    private get oddRowColor(): Color {
        return this._oddRowColor;
    }

    private set oddRowColor(value: Color) {
        if (value === this._oddRowColor) {
            return;
        }
        this._oddRowColor = value;
        this.sourceUserSettings.activeRow.set("table_odd_row_color", this.oddRowColor);
        // Don't set color when in the designer...we don't want to serialize that color as part of the layout
        if (this._designer == null) {
            this.panelOddColorDisplay.backgroundColor = this._oddRowColor ?? "";
        }
    }

    sourceUserSettingsAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() === DataSourceAction.SEARCH) {
            this.evenRowColor = this.sourceUserSettings.activeRow.get("table_even_row_color", undefined);
            this.oddRowColor = this.sourceUserSettings.activeRow.get("table_odd_row_color", undefined);
        }
    }

    private static getLog(): Logger {
        if (this.logger == null) {
            this.logger = LogManager.getLogger("common.user.LMEUserGeneral");
        }
        return this.logger;
    }

    switchIsActiveOnChange() {
        if (this.sourceUserSettings.isAddingOrUpdating()) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            if (this.switchIsActive.checked) {
                if (this.textboxActiveDate.isEmpty()) {
                    this.sourceUserSettings.activeRow?.set("active_date", today);
                }
                this.sourceUserSettings.activeRow?.set("inactive_date", null);
            } else {
                if (this.textboxInactiveDate.isEmpty()) {
                    this.sourceUserSettings.activeRow?.set("inactive_date", today);
                }
            }
        }
    }

    async switchEmailOverrideCompanyOnChange(event: ChangeEvent) {
        const showingEmailOverrideComponents = this.showOverrideEmailComponents();
        this.syncEmailOverrideFieldVisibility(showingEmailOverrideComponents);

        if (event.userInitiatedChange) {
            if (!showingEmailOverrideComponents) {
                // If not showing the override components, clear the textboxes (other than the email address)
                this.layoutEmailSettings.components.filter(component => {
                    return component instanceof Textbox &&
                        component !== this.layoutEmailSettings.textboxEmail;
                }).forEach(component => (component as Textbox).clear());
            } else if (this.layoutEmailSettings.textboxEmailProtocol.isEmpty()) {
                // If show the override components, and they haven't been supplied, default them from the company
                // settings.  Test one of the required fields to see if they have been supplied (above test on
                // textboxEmailProtocol).
                const filter = { id: CompanySettings.get().id };
                const fieldList = {
                    layoutName: "common/email/EmailSettings",
                    extraFields: "common/email/company-email-settings"
                };
                const companyRow = await Model.searchSingleRecord("common/company", filter, fieldList);
                if (companyRow != null) {
                    delete companyRow.data.id;
                    delete companyRow.data.email_address;
                    delete companyRow.data.email_password;
                    this.sourceUserSettings.activeRow.set(companyRow.data);
                }
            }
        }
    }

    private syncEmailOverrideFieldVisibility(value: boolean = this.showOverrideEmailComponents()) {
        this.separatorPanel.visible = value;
        this.panelUserGeneralRight.visible = value;
        // Ensure the 'required' email fields are only required if the override is in use
        this.layoutEmailSettings.requireFields(value);
    }

    private showOverrideEmailComponents(): boolean {
        return this.switchEmailOverrideCompany.checked === true && UserUtil.userHasUserListAccess();
    }

    textboxTerminalIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_terminal = "Y";
    }
}
